import React, { useState, useEffect, useCallback } from "react";
import {
  Page,
  Layout,
  Card,
  TextField,
  Checkbox,
  Button,
  Banner,
  Spinner,
  Text,
  DropZone,
  BlockStack,
  Thumbnail,
  Box,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import { useShop } from "../context/ShopContext.jsx";
import { useI18n } from "../context/I18nContext.jsx";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function LandingPageTemplatePage() {
  const navigate = useNavigate();
  const { apiFetch, shopDomain } = useShop();
  const { t } = useI18n();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [title, setTitle] = useState("");
  const [introText, setIntroText] = useState("");
  const [sizeGuideText, setSizeGuideText] = useState("");
  const [additionalText, setAdditionalText] = useState("");
  const [additionalImages, setAdditionalImages] = useState([]);
  const [showSizeCalculator, setShowSizeCalculator] = useState(true);
  const [orderButtonText, setOrderButtonText] = useState("Bestelling afronden");
  const [orderButtonUrl, setOrderButtonUrl] = useState("");

  const loadTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/landing-page-template");
      setTitle(data.title || "");
      setIntroText(data.introText || "");
      setSizeGuideText(data.sizeGuideText || "");
      setAdditionalText(data.additionalText || "");
      setAdditionalImages(JSON.parse(data.additionalImages || "[]"));
      setShowSizeCalculator(data.showSizeCalculator !== false);
      setOrderButtonText(data.orderButtonText || "Bestelling afronden");
      setOrderButtonUrl(data.orderButtonUrl || "");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate, shopDomain]);

  const handleAddImage = useCallback(async (_dropped, accepted) => {
    for (const file of accepted) {
      const dataUrl = await fileToBase64(file);
      setAdditionalImages((prev) => [...prev, dataUrl]);
    }
  }, []);

  const handleRemoveImage = (idx) => {
    setAdditionalImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiFetch("/landing-page-template", {
        method: "PUT",
        body: JSON.stringify({
          title,
          introText,
          sizeGuideText,
          additionalText,
          additionalImages: JSON.stringify(additionalImages),
          showSizeCalculator,
          orderButtonText,
          orderButtonUrl: orderButtonUrl || null,
        }),
      });
      setSuccess(t("landingTemplate.saved"));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page title={t("landingTemplate.title")}>
        <Spinner />
      </Page>
    );
  }

  return (
    <Page
      title={t("landingTemplate.title")}
      primaryAction={{
        content: saving ? t("common.saving") : t("common.save"),
        onAction: handleSave,
        loading: saving,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}
        {success && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccess(null)}>
              {success}
            </Banner>
          </Layout.Section>
        )}

        {/* Info banner */}
        <Layout.Section>
          <Banner tone="info">
            {t("landingTemplate.infoBanner")}
          </Banner>
        </Layout.Section>

        {/* Page title & intro */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">{t("landingTemplate.titleSection")}</Text>
                <TextField
                  label={t("landingTemplate.pageTitle")}
                  value={title}
                  onChange={setTitle}
                  placeholder={t("landingTemplate.pageTitlePlaceholder")}
                  helpText={t("landingTemplate.pageTitleHelp")}
                />
                <TextField
                  label={t("landingTemplate.introText")}
                  value={introText}
                  onChange={setIntroText}
                  multiline={4}
                  placeholder={t("landingTemplate.introPlaceholder")}
                  helpText={t("landingTemplate.introHelp")}
                />
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Size guide */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">{t("landingTemplate.sizeSection")}</Text>
                <Checkbox
                  label={t("landingTemplate.showCalculator")}
                  checked={showSizeCalculator}
                  onChange={setShowSizeCalculator}
                />
                <TextField
                  label={t("landingTemplate.sizeGuide")}
                  value={sizeGuideText}
                  onChange={setSizeGuideText}
                  multiline={4}
                  placeholder={t("landingTemplate.sizeGuidePlaceholder")}
                />
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Extra content */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">{t("landingTemplate.extraSection")}</Text>
                <TextField
                  label={t("landingTemplate.additionalText")}
                  value={additionalText}
                  onChange={setAdditionalText}
                  multiline={4}
                  placeholder={t("landingTemplate.additionalTextPlaceholder")}
                />
                <Text variant="bodyMd" fontWeight="semibold">
                  {t("landingTemplate.extraImages")}
                </Text>
                <DropZone accept="image/*" type="image" onDrop={handleAddImage}>
                  <DropZone.FileUpload actionTitle={t("landingTemplate.addImage")} actionHint={t("landingTemplate.dropImage")} />
                </DropZone>
                {additionalImages.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                    {additionalImages.map((src, idx) => (
                      <div key={idx} style={{ position: "relative" }}>
                        <Thumbnail source={src} alt={`Extra afbeelding ${idx + 1}`} size="large" />
                        <button
                          onClick={() => handleRemoveImage(idx)}
                          style={{
                            position: "absolute",
                            top: -6,
                            right: -6,
                            background: "#e44",
                            color: "#fff",
                            border: "none",
                            borderRadius: "50%",
                            width: 20,
                            height: 20,
                            cursor: "pointer",
                            fontSize: 12,
                            lineHeight: "20px",
                            padding: 0,
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Order button */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">{t("landingTemplate.orderSection")}</Text>
                <TextField
                  label={t("landingTemplate.orderButtonText")}
                  value={orderButtonText}
                  onChange={setOrderButtonText}
                  placeholder={t("landingTemplate.orderButtonTextPlaceholder")}
                />
                <TextField
                  label={t("landingTemplate.orderButtonUrl")}
                  value={orderButtonUrl}
                  onChange={setOrderButtonUrl}
                  placeholder="https://uw-winkel.myshopify.com/cart/..."
                  helpText={t("landingTemplate.orderButtonUrlHelp")}
                />
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
